/**
 * PageTools — Register / Unregister Engine (client-side only)
 *
 * Publishes tools to two targets:
 *   1. window.PageTools        — polled by ngd-conv-ai / VzGPT AI clients
 *   2. navigator.modelContext  — native WebMCP browser API (Chrome Canary / DevTrial)
 *
 * Assumes a browser environment (`window` and `navigator` are always present).
 *
 * @module utils/pageToolsHelper
 */

/**
 * Checks whether navigator.modelContext is a native WebMCP object with registerTool().
 * @param {unknown} modelContext
 * @returns {boolean}
 */
function isNativeWebMCP(modelContext) {
  return !!modelContext && typeof modelContext === 'object' && typeof modelContext.registerTool === 'function';
}

/**
 * Publishes the given tools to window.PageTools and navigator.modelContext.
 *
 * @param {Array<{ name: string, description: string, inputSchema: object, execute: function }>} tools
 * @returns {void}
 */
export function registerTools(tools) {
  window.PageTools = { tools, isReady: true };
  window.__PageToolsWebMcpRegisterErrors = [];

  if (!navigator.modelContext) {
    try {
      Object.defineProperty(navigator, 'modelContext', {
        value: { tools },
        configurable: true,
        writable: true,
      });
    } catch (e) {
      // best-effort polyfill — ignore if property is sealed
    }
    return;
  }

  try {
    navigator.modelContext.tools = tools;
  } catch (e) {
    // ignore read-only guard on native object
  }

  if (isNativeWebMCP(navigator.modelContext)) {
    tools.forEach((tool) => {
      try {
        const maybePromise = navigator.modelContext.registerTool({
          name: tool.name,
          description: tool.description,
          inputSchema: tool.inputSchema,
          execute(params) {
            return tool.execute(params);
          },
          handler(params) {
            return tool.execute(params);
          },
        });
        if (maybePromise && typeof maybePromise.catch === 'function') {
          maybePromise.catch((err) => {
            const entry = { tool: tool.name, error: String((err && err.message) || err) };
            window.__PageToolsWebMcpRegisterErrors = (window.__PageToolsWebMcpRegisterErrors || []).concat([entry]);
            console.warn('[PageTools][WebMCP] registerTool rejected:', entry);
          });
        }
      } catch (err) {
        const entry = { tool: tool.name, error: String((err && err.message) || err) };
        window.__PageToolsWebMcpRegisterErrors = (window.__PageToolsWebMcpRegisterErrors || []).concat([entry]);
        console.warn('[PageTools][WebMCP] registerTool threw:', entry);
      }
    });
  }
}

/**
 * Clears all registered tools from window.PageTools and navigator.modelContext.
 * Called on component unmount to prevent stale tools being exposed.
 *
 * @returns {void}
 */
export function unregisterTools() {
  window.PageTools = { tools: [], isReady: false };
  if (!navigator.modelContext) return;
  try {
    navigator.modelContext.tools = [];
  } catch (e) {
    // ignore read-only guard
  }
}
